import { Card, Box, Typography } from "@mui/material";
import { CheckCircle, CircleOutlined } from "@mui/icons-material";
import { colors } from "../../../../../../../ui/colors";

const COLORS = {
  orange: {
    base: colors.orange.base,
  },
  gray: {
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    600: "#4B5563",
    800: "#111827",
  },
};

interface SpecialRequest {
  uid: string;
  name: string;
  imageUrl?: string;
  description?: string;
}

interface SpecialRequestCardProps {
  request: SpecialRequest;
  selected: boolean;
  onSelect: () => void;
}

function cleanDescription(description = "") {
  return description
    .replace(/\s*@seed\b\s*/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const SpecialRequestCard = ({ request, selected, onSelect }: SpecialRequestCardProps) => {
  const hasImage = Boolean(request?.imageUrl);

  return (
    <Card
      onClick={onSelect}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        borderRadius: 3,
        border: selected ? `2px solid ${COLORS.orange.base}` : `1px solid ${COLORS.gray[200]}`,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        cursor: "pointer",
        p: 2,
        mb: 1,
        transition: "all 0.25s ease",
        boxShadow: selected ? `0 0 0 3px rgba(255, 120, 0, 0.15)` : "0 1px 3px rgba(0,0,0,0.04)",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingBlock: 1,
          }}
        >
          {hasImage ? (
            <Box
              component="img"
              src={request.imageUrl}
              alt={request.name}
              sx={{
                maxWidth: "110%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: COLORS.gray[400],
                textAlign: "center",
                px: 1,
                lineHeight: 1.2,
              }}
            >
              No image
            </Typography>
          )}
        </Box>

        <Box flex={1} minWidth={0} marginTop={-2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                color: COLORS.gray[800],
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {request.name}
            </Typography>

            {selected ? (
              <CheckCircle
                sx={{
                  color: COLORS.orange.base,
                  flexShrink: 0,
                }}
              />
            ) : (
              <CircleOutlined
                sx={{
                  color: COLORS.gray[400],
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              mt: 1,
              mb: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {cleanDescription(request?.description)}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};
